/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { graphql } from 'react-apollo';
import { Link as ReactRouterLink } from 'react-router';
import Waypoint from 'react-waypoint';

import { compose, setDisplayName, withStateHandlers } from 'recompose';

import Retracked from 'js/lib/retracked';

import { Button, Grid, Link, PageGridContainer, Typography2, useTheme } from '@coursera/cds-core';
import type { ButtonProps, LinkProps } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';
import { IdTypes } from 'bundles/product-features';
import { Heading, Section } from 'bundles/program-home/components/AutoHeading';
import ProgramCollectionsView from 'bundles/program-home/components/enterprise-home/ProgramCollectionsView';
import SkillsFocus from 'bundles/program-home/components/enterprise-home/SkillsFocus';
import type { EnterpriseHomePageQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_enterprisePrograms_elements as EnterpriseProgram } from 'bundles/program-home/components/enterprise-home/__generated__/EnterpriseHomePageQuery';
import SkillSetCards from 'bundles/program-home/components/multiprogram/SkillSetCards';
import { CollectionsView } from 'bundles/program-home/components/single-program/CuratedCollections';
import ForCreditCollection from 'bundles/program-home/components/single-program/ForCreditCollection';
import type {
  AcademicDisciplineNameQueryVariables,
  AcademicDisciplineSummariesResult,
} from 'bundles/program-home/components/single-program/SingleProgramGraphqlQueries';
import { AcademicDisciplineNameQuery } from 'bundles/program-home/components/single-program/SingleProgramGraphqlQueries';
import { withProductFeatures } from 'bundles/program-home/productFeatureFlags';
import type { OnProductCardClick } from 'bundles/program-home/types/Products';

import _t from 'i18n!nls/program-home';

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps<ReactRouterLink>>(
  // TODO(ppaskaris): Ask how to type this.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  Link
);

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps<ReactRouterLink>>(
  // TODO(ppaskaris): Ask how to type this.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  Button
);

type PropsFromCaller = {
  enterpriseProgram: EnterpriseProgram;
  isAuthenticatedUser: boolean;
  onProductCardClick: OnProductCardClick;
  lazy?: boolean;
  isVisible: boolean;
  enableAcademicDiscipline?: boolean;
  allowOrgForCurriculumBuilder?: boolean;
  allowOrgForSpecializationConfiguration?: boolean;
  showSkillSetRoleFilter: boolean;
};

type PropsFromProductFeatures = { enableSkillsDashboard: boolean };

type PropsFromGraphql = {
  academicDisciplineName?: string;
};

type StateFromLazyLoad = {
  isLazyPlaceholder: boolean;
};

type StateHandlersFromLazyLoad = {
  lazyLoad: () => StateFromLazyLoad;
};

type Props = PropsFromCaller &
  StateFromLazyLoad &
  StateHandlersFromLazyLoad &
  PropsFromGraphql &
  PropsFromProductFeatures;

export function ProgramContainerRenderer({
  enterpriseProgram,
  isAuthenticatedUser,
  onProductCardClick,
  lazy,
  isVisible,
  lazyLoad,
  isLazyPlaceholder,
  academicDisciplineName,
  allowOrgForCurriculumBuilder,
  allowOrgForSpecializationConfiguration,
  showSkillSetRoleFilter,
  enableSkillsDashboard,
}: Props) {
  const theme = useTheme();

  const hasTSPs =
    enterpriseProgram.metadata.linkedTargetSkillProfileIds.length > 0 ||
    (EnterpriseExperiments.get('enableSkillSetsByDefaultDGS') &&
      enterpriseProgram.metadata.skillSetAvailabilityType === 'ALL_SKILLSETS');
  if (!enterpriseProgram) {
    return null;
  }

  const { name: programName, slug: programSlug } = enterpriseProgram.metadata;
  const styles = {
    title: css`
      display: flex;
      flex-flow: row wrap;
      align-items: center;
      border-bottom: 1px solid ${theme.palette.gray[500]};
      padding-bottom: ${theme.spacing(12)};
      margin-bottom: ${theme.spacing(16)};
    `,
    titleHeading: css`
      flex: 1 1 auto;
    `,
    titleLink: css`
      flex: 0 1 auto;
    `,
    description: css`
      margin-bottom: ${theme.spacing(32)};
      max-width: 712px;
    `,
    descriptionText: css`
      margin-right: ${theme.spacing(8)};
    `,
    helpButton: css`
      margin-left: calc(${theme.spacing(8)} * -1);
    `,
    skillSets: css`
      margin-bottom: ${theme.spacing(64)};
    `,
    buttonWrapper: css`
      display: flex;
      justify-content: center;
      margin-top: ${theme.spacing(48)};
    `,
    gridContainer: css`
      padding: 0 !important; /* override PageGridContainer padding so content is aligned */
    `,
  };

  const skillSetsTitle = academicDisciplineName
    ? _t('SkillSets for the most popular job roles in #{academicDisciplineName}', { academicDisciplineName })
    : _t('SkillSets recommended by your organization');

  return (
    <div className="rc-ProgramContainer">
      <PageGridContainer css={styles.gridContainer}>
        <Grid item xs={12}>
          {lazy && isVisible ? <Waypoint onEnter={lazyLoad} /> : null}
          <div css={styles.title}>
            <Heading defaultLevel={2} css={styles.titleHeading}>
              {programName}
            </Heading>
            <TrackedLink
              {...Link.defaultProps}
              css={styles.titleLink}
              variant="quiet"
              typographyVariant="subtitleLarge"
              component={ReactRouterLink}
              trackingName="explore_full_program_link"
              to={`/programs/${programSlug}`}
              aria-label={_t('Explore the full learning program: #{programName}', { programName })}
            >
              {_t('Explore the full learning program')}
            </TrackedLink>
          </div>
          <Section initialLevel={3}>
            <div css={styles.description}>
              <Typography2 component="p" css={styles.descriptionText}>
                {_t(
                  'Enroll in any course from this learning program. A learning program is a repository of content curated by your organization.'
                )}
              </Typography2>
            </div>
            {(!!academicDisciplineName || allowOrgForCurriculumBuilder) &&
              (isLazyPlaceholder ? (
                <CollectionsView.Placeholder length={1} />
              ) : (
                <ForCreditCollection
                  headingLevel={3}
                  programId={enterpriseProgram.id}
                  onProductCardClick={onProductCardClick}
                  allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
                />
              ))}
            {enableSkillsDashboard && (
              <SkillsFocus skillLimit={6} programSlug={programSlug} programId={enterpriseProgram.id} />
            )}

            {hasTSPs && (
              <div css={styles.skillSets}>
                {isLazyPlaceholder ? (
                  <SkillSetCards.Placeholder
                    programSlug={programSlug}
                    title={skillSetsTitle}
                    programName={enterpriseProgram.metadata.name}
                    renderOrgHomeVariant
                    skillSetLength={3}
                    showSkillSetRoleFilter={showSkillSetRoleFilter}
                  />
                ) : (
                  <SkillSetCards
                    programId={enterpriseProgram.id}
                    programSlug={programSlug}
                    title={skillSetsTitle}
                    programName={enterpriseProgram.metadata.name}
                    renderOrgHomeVariant
                    showCarousel
                    skillSetLength={3}
                    showSkillSetRoleFilter={showSkillSetRoleFilter}
                  />
                )}
              </div>
            )}
            {isLazyPlaceholder ? (
              <ProgramCollectionsView.Placeholder />
            ) : (
              <ProgramCollectionsView
                programId={enterpriseProgram.id}
                programSlug={enterpriseProgram.metadata.slug}
                programName={enterpriseProgram.metadata.name}
                isAuthenticatedUser={isAuthenticatedUser}
                onProductCardClick={onProductCardClick}
                academicDisciplineName={academicDisciplineName}
                allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
                enableCurriculumBuilder={allowOrgForCurriculumBuilder}
              />
            )}
            <div css={styles.buttonWrapper}>
              <TrackedButton
                {...Button.defaultProps}
                component={ReactRouterLink}
                variant="primary"
                trackingName="explore_full_program_button"
                to={`/programs/${programSlug}`}
                aria-label={_t('Explore the full learning program: #{programName}', { programName })}
                icon={<ArrowNextIcon />}
                iconPosition="after"
              >
                {_t('Explore the full learning program')}
              </TrackedButton>
            </div>
            {lazy && isVisible ? <Waypoint onEnter={lazyLoad} /> : null}
          </Section>
        </Grid>
      </PageGridContainer>
    </div>
  );
}

const ProgramContainerHookProxy = (props: Props) => <ProgramContainerRenderer {...props} />;

const enhance = compose<Props, PropsFromCaller>(
  setDisplayName('ProgramContainer'),
  Retracked.createTrackedContainer<PropsFromCaller>(({ enterpriseProgram }) => ({ programId: enterpriseProgram.id })),
  withStateHandlers<StateFromLazyLoad, StateHandlersFromLazyLoad, PropsFromCaller>(
    ({ lazy }) => ({ isLazyPlaceholder: lazy === true }),
    {
      lazyLoad: () => () => ({ isLazyPlaceholder: false }),
    }
  ),
  withProductFeatures<PropsFromProductFeatures, PropsFromCaller>(
    ({ enterpriseProgram }) => ({
      idType: IdTypes.Program,
      id: enterpriseProgram.id,
    }),
    ({ features }) => ({
      enableSkillsDashboard: features.get('enterprise', 'enableSkillsDashboard'),
    })
  ),
  graphql<PropsFromCaller, AcademicDisciplineSummariesResult, AcademicDisciplineNameQueryVariables, PropsFromGraphql>(
    AcademicDisciplineNameQuery,
    {
      skip: ({ enableAcademicDiscipline, enterpriseProgram }) =>
        !enableAcademicDiscipline || !enterpriseProgram?.metadata?.linkedAcademicDisciplineIds?.length,
      options: ({ enterpriseProgram }) => {
        return {
          // Id is checked for in the skip above
          variables: { disciplineId: enterpriseProgram?.metadata.linkedAcademicDisciplineIds?.[0] },
          context: { clientName: 'gatewayGql' },
        };
      },
      props: ({ data }) => {
        const academicDisciplineName = data?.AcademicDiscipline?.queryAcademicDisciplineById.name;
        return { academicDisciplineName };
      },
    }
  )
);

export default enhance(ProgramContainerHookProxy);
